import React, { FunctionComponent, Fragment, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { differenceInMinutes, isSameDay } from "date-fns";
import { ChatMessage, ChatPhase } from "../../context/ChatContext";
import { whiteText } from "../../styles/theme";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatDateDivider from "./ChatDateDivider";
import TypingIndicator from "./TypingIndicator";

const GROUP_GAP_MINUTES = 5;

const startsNewDay = (message: ChatMessage, previous?: ChatMessage) =>
	!previous || !isSameDay(previous.timestamp, message.timestamp);

const endsGroup = (message: ChatMessage, next?: ChatMessage) =>
	!next ||
	next.author !== message.author ||
	differenceInMinutes(next.timestamp, message.timestamp) >= GROUP_GAP_MINUTES;

interface ChatMessagesProps {
	messages: ChatMessage[];
	phase: ChatPhase;
	isBotTyping: boolean;
}

const ChatMessages: FunctionComponent<ChatMessagesProps> = ({
	messages,
	phase,
	isBotTyping,
}) => {
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, phase, isBotTyping]);

	return (
		<Box
			sx={{
				flex: 1,
				overflowY: "auto",
				padding: "16px",
				display: "flex",
				flexDirection: "column",
				gap: "10px",
				backgroundColor: whiteText,
			}}
		>
			{messages.map((message, index) => (
				<Fragment key={index}>
					{startsNewDay(message, messages[index - 1]) && (
						<ChatDateDivider timestamp={message.timestamp} />
					)}
					<ChatMessageBubble
						message={message}
						showTimestamp={endsGroup(message, messages[index + 1])}
					/>
				</Fragment>
			))}
			{(phase === ChatPhase.LOADING || isBotTyping) && <TypingIndicator />}
			<div ref={messagesEndRef} />
		</Box>
	);
};

export default ChatMessages;
